import React, { ReactNode, forwardRef } from "react";
import clsx from "clsx";
import { Check, ArrowRight, Star, Heart, AlertCircle, Info } from "lucide-react";

// List Types
export interface ListProps {
  children: ReactNode;
  ordered?: boolean;
  icon?: ReactNode;
  hoverable?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  as?: "ul" | "ol" | "div";
}

export interface ListItemProps {
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  hoverable?: boolean;
  size?: "sm" | "md" | "lg";
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
  onClick?: () => void;
}

export interface MediaItemProps {
  avatar?: string;
  image?: string;
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  description?: string;
  active?: boolean;
  disabled?: boolean;
  hoverable?: boolean;
  size?: "sm" | "md" | "lg";
  suffix?: ReactNode;
  className?: string;
  onClick?: () => void;
}

export interface DescriptionListProps {
  children: ReactNode;
  className?: string;
}

export interface DescriptionTermProps {
  children: ReactNode;
  className?: string;
}

export interface DescriptionDetailsProps {
  children: ReactNode;
  className?: string;
}

// Base List Component
export const List = forwardRef<HTMLUListElement | HTMLOListElement | HTMLDivElement, ListProps>(
  (
    { children, ordered = false, icon, hoverable = false, size = "md", className, as, ...props },
    ref,
  ) => {
    const sizeStyles = {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    };

    const Component = as || (ordered ? "ol" : "ul");

    const listClasses = clsx(
      "space-y-1",
      sizeStyles[size],
      {
        "list-none": !ordered,
        "list-decimal": ordered,
      },
      className,
    );

    return (
      <Component
        ref={
          ref as React.Ref<HTMLDivElement> &
            React.Ref<HTMLOListElement> &
            React.Ref<HTMLUListElement>
        }
        className={listClasses}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              hoverable,
              size,
              icon,
            } as Record<string, unknown>);
          }
          return child;
        })}
      </Component>
    );
  },
);

List.displayName = "List";

// List Item Component
export const ListItem = forwardRef<HTMLLIElement | HTMLDivElement, ListItemProps>(
  (
    {
      children,
      active = false,
      disabled = false,
      hoverable = false,
      size = "md",
      prefix,
      suffix,
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const sizeStyles = {
      sm: "py-1 px-2",
      md: "py-2 px-3",
      lg: "py-3 px-4",
    };

    const itemClasses = clsx(
      "flex items-center gap-3 rounded-md transition-colors",
      sizeStyles[size],
      {
        "text-gray-200": !active && !disabled,
        "bg-gray-800 text-tracq-bright-blue": active,
        "text-gray-500 cursor-not-allowed": disabled,
        "hover:bg-gray-800 cursor-pointer": hoverable && !disabled && !active,
      },
      className,
    );

    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    return (
      <li
        ref={ref as React.Ref<HTMLLIElement>}
        className={itemClasses}
        onClick={handleClick}
        {...props}
      >
        {prefix && <div className="flex-shrink-0">{prefix}</div>}
        <div className="flex-1">{children}</div>
        {suffix && <div className="flex-shrink-0">{suffix}</div>}
      </li>
    );
  },
);

ListItem.displayName = "ListItem";

// Ordered List Component
export const OrderedList: React.FC<Omit<ListProps, "ordered">> = (props) => {
  return <List {...props} ordered />;
};

// Unordered List Component
export const UnorderedList: React.FC<Omit<ListProps, "ordered">> = (props) => {
  return <List {...props} ordered={false} />;
};

// Icon List Component
interface IconListProps extends Omit<ListProps, "icon"> {
  icon: ReactNode;
}

export const IconList: React.FC<IconListProps> = ({ icon, ...props }) => {
  return <List {...props} icon={icon} />;
};

// Action List Component
interface ActionListProps {
  items: Array<{
    label: string;
    icon?: ReactNode;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
  }>;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const ActionList: React.FC<ActionListProps> = ({ items, size = "md", className }) => {
  return (
    <List hoverable size={size} {...(className !== undefined ? { className } : {})}>
      {items.map((item, index) => (
        <ListItem
          key={index}
          {...(item.active !== undefined ? { active: item.active } : {})}
          {...(item.disabled !== undefined ? { disabled: item.disabled } : {})}
          hoverable
          size={size}
          prefix={item.icon}
          {...(item.onClick ? { onClick: item.onClick } : {})}
        >
          {item.label}
        </ListItem>
      ))}
    </List>
  );
};

// Media List Component
export const MediaList: React.FC<Omit<ListProps, "children"> & { children: ReactNode }> = (
  props,
) => {
  return <List {...props} />;
};

// Media Item Component
export const MediaItem: React.FC<MediaItemProps> = ({
  avatar,
  image,
  icon,
  title,
  subtitle,
  description,
  active = false,
  disabled = false,
  hoverable = false,
  size = "md",
  suffix,
  className,
  onClick,
}) => {
  const sizeStyles = {
    sm: "py-2 px-3",
    md: "py-3 px-4",
    lg: "py-4 px-5",
  };

  const avatarSizes = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  };

  const itemClasses = clsx(
    "flex items-center gap-3 rounded-md transition-colors",
    sizeStyles[size],
    {
      "text-gray-200": !active && !disabled,
      "bg-gray-800 text-tracq-bright-blue": active,
      "text-gray-500 cursor-not-allowed": disabled,
      "hover:bg-gray-800 cursor-pointer": hoverable && !disabled && !active,
    },
    className,
  );

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <li className={itemClasses} onClick={handleClick}>
      {/* Avatar/Image/Icon */}
      <div className={clsx("flex-shrink-0", avatarSizes[size])}>
        {avatar ? (
          <img
            src={avatar}
            alt={title}
            className={clsx("rounded-full object-cover", avatarSizes[size])}
          />
        ) : image ? (
          <img
            src={image}
            alt={title}
            className={clsx("rounded-md object-cover", avatarSizes[size])}
          />
        ) : icon ? (
          <div
            className={clsx("flex items-center justify-center text-gray-400", avatarSizes[size])}
          >
            {icon}
          </div>
        ) : (
          <div
            className={clsx(
              "bg-gray-700 rounded-full flex items-center justify-center text-gray-400",
              avatarSizes[size],
            )}
          >
            <span className="text-xs font-medium">{title.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-200 truncate">{title}</div>
        {subtitle && <div className="text-sm text-gray-400 truncate">{subtitle}</div>}
        {description && (
          <div className="text-xs text-gray-500 mt-1 line-clamp-2">{description}</div>
        )}
      </div>

      {/* Suffix */}
      {suffix && <div className="flex-shrink-0">{suffix}</div>}
    </li>
  );
};

// Description List Component
export const DescriptionList: React.FC<DescriptionListProps> = ({ children, className }) => {
  return <dl className={clsx("space-y-2", className)}>{children}</dl>;
};

// Description Term Component
export const DescriptionTerm: React.FC<DescriptionTermProps> = ({ children, className }) => {
  return <dt className={clsx("text-sm font-medium text-gray-400", className)}>{children}</dt>;
};

// Description Details Component
export const DescriptionDetails: React.FC<DescriptionDetailsProps> = ({ children, className }) => {
  return <dd className={clsx("text-sm text-gray-200", className)}>{children}</dd>;
};

// Specialized List Components
export const CheckList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<Check className="w-4 h-4 text-green-400" />} />;
};

export const ArrowList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<ArrowRight className="w-4 h-4 text-gray-400" />} />;
};

export const StarList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<Star className="w-4 h-4 text-yellow-400" />} />;
};

export const HeartList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<Heart className="w-4 h-4 text-red-400" />} />;
};

export const AlertList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<AlertCircle className="w-4 h-4 text-orange-400" />} />;
};

export const InfoList: React.FC<Omit<ListProps, "icon">> = (props) => {
  return <List {...props} icon={<Info className="w-4 h-4 text-blue-400" />} />;
};
